import { useTheme } from '@material-ui/core'
import { WorkingStepIconSvg } from 'components/Common/WorkingSteps/WorkingStepIcon'
import { isEqual } from 'lodash'
import { MouseEvent } from 'react'
import { useAppSelector } from 'store/configureStore'
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  XAxis,
  XAxisProps,
  YAxis,
} from 'recharts'
import {
  MoneyDto,
  PriceSummaryDto,
  WorkingStepType,
} from 'services/APIs/InternalAPI/internal-api.contracts'
import { RootAppState } from 'store/configureStore'
import { MoneyAxisTick } from '../../../Graph/YAxisTick'

const WorkingStepXAxisTick = (
  props: {
    onMouseEnter: (e: MouseEvent, index: number) => void
    onMouseLeave: (e: MouseEvent) => void
    onClick: (index: number) => void
    payload: { index: number; value: WorkingStepType }
  } & XAxisProps
) => {
  const { x, y, payload } = props

  return (
    <g
      transform={`translate(${x},${y})`}
      onClick={() => {
        props.onClick(payload.index)
      }}
      style={{ cursor: 'pointer' }}
    >
      <rect
        width={'28px'}
        height={'42px'}
        x={-16}
        onMouseEnter={(e) => {
          requestAnimationFrame(() => props.onMouseEnter(e, payload.index))
        }}
        onMouseLeave={(e) => props.onMouseLeave(e)}
        fill={'none'}
        y={-4}
        z={1}
        pointerEvents="all"
      />
      <WorkingStepIconSvg
        workingStepType={payload.value}
        attributes={{
          width: '28px',
          height: '28px',
          pointerEvents: 'none',
          x: -16,
          y: -4,
          textAnchor: 'end',
        }}
      />
    </g>
  )
}

type GraphData = {
  materialId: string
  totalSalesPrice: MoneyDto
}

export const WorkingStepsBarChart = (props: {
  priceSummaryKeys: Array<string>
  handleMouseEnter: (e: MouseEvent, priceSummary: PriceSummaryDto) => void
  scrollAndFilter: (priceSummary: PriceSummaryDto) => void
  containerHeight?: number
}) => {
  const theme = useTheme()

  const priceSummariesSum: Array<GraphData> = useAppSelector(
    (state: RootAppState) => {
      const graphData: Array<GraphData> = props.priceSummaryKeys.reduce(
        (acc, key) => {
          const priceSummary = state.project.priceSummaries[key]

          if (!priceSummary?.isWorkingStep) {
            return acc
          }

          acc.push({
            id: priceSummary.workingStep.primaryWorkingStep,
            workingStep: priceSummary.workingStep,
            description: priceSummary.description,
            totalSalesPrice: priceSummary.totalSalesPrice,
            totalCostPrice: priceSummary.totalCostPrice,
            productionTime: priceSummary.productionTime,
            surchargeValue: priceSummary.surchargeValue,
            discountValue: {
              ...priceSummary.discountValue,
              value: priceSummary.discountValue.value * -1,
            },
            totalQuantity: priceSummary.totalQuantity,
            staggeredTotalQuantity: priceSummary.staggeredTotalQuantity,
            isWorkingStep: true,
          })

          return acc
        },
        []
      )

      graphData.length &&
        graphData.sort(
          (a, b) => b.totalSalesPrice?.value - a.totalSalesPrice?.value
        )

      return graphData
    },
    isEqual
  )

  return (
    <ResponsiveContainer width={'100%'} height={props.containerHeight || 204}>
      <BarChart data={priceSummariesSum}>
        {/* <Tooltip /> */}
        <CartesianGrid strokeDasharray={'3 3'} />
        <XAxis
          dataKey="id"
          type="category"
          interval={0}
          tickMargin={12}
          height={42}
          tick={(_props) => (
            <WorkingStepXAxisTick
              {..._props}
              onMouseEnter={(e, index) =>
                props.handleMouseEnter(e, priceSummariesSum[index])
              }
              onMouseLeave={(e) => props.handleMouseEnter(e, undefined)}
              onClick={(index) =>
                props.scrollAndFilter(priceSummariesSum[index])
              }
            />
          )}
        />
        <YAxis
          type="number"
          // ticks={priceSummaries
          //   .map((x) => x.totalSalesPrice.value)
          //   .sort()
          //   .concat(projectNetSalesPrice.value)}
          tick={
            <MoneyAxisTick
              defaultMoney={priceSummariesSum[0].totalSalesPrice}
            />
          }
          ticks={priceSummariesSum.map((x) => x.totalSalesPrice?.value)}
          minTickGap={10}
          interval={'preserveStartEnd'}
          allowDataOverflow={false}
          domain={[0, 'dataMax']}
        />
        <Bar
          dataKey={'totalCostPrice.value'}
          fill={theme.palette.info.light}
          onMouseEnter={(data, _index, e) => props.handleMouseEnter(e, data)}
          onMouseLeave={(_data, _index, e) =>
            props.handleMouseEnter(e, undefined)
          }
          barSize={32}
          onClick={(data) => props.scrollAndFilter(data)}
          stackId="a"
          style={{
            cursor: 'pointer',
          }}
        />
        <Bar
          dataKey={'surchargeValue.value'}
          fill={theme.palette.success.main}
          onMouseEnter={(data, _index, e) => props.handleMouseEnter(e, data)}
          onMouseLeave={(_data, _index, e) =>
            props.handleMouseEnter(e, undefined)
          }
          barSize={32}
          onClick={(data) => props.scrollAndFilter(data)}
          stackId="a"
          style={{
            cursor: 'pointer',
          }}
        />
        <Bar
          dataKey={'discountValue.value'}
          fill={theme.palette.error.main}
          onMouseEnter={(data, _index, e) => props.handleMouseEnter(e, data)}
          onMouseLeave={(_data, _index, e) =>
            props.handleMouseEnter(e, undefined)
          }
          barSize={32}
          onClick={(data) => props.scrollAndFilter(data)}
          stackId="a"
          style={{
            cursor: 'pointer',
          }}
        />
      </BarChart>
    </ResponsiveContainer>
  )
}
